import { useState, useEffect, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import Header from "@/pages/home/components/Header";
import Footer from "@/pages/home/components/Footer";

interface CommentItem {
  id: number;
  content: string;
  created_at: string;
  post_id: number;
  post_title: string;
  post_category: string;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const categoryLabels: Record<string, string> = {
  community: "자유게시판",
  issue: "실시간 이슈",
  scam_report: "먹튀제보",
  safety: "안전정보",
  sports: "스포츠분석",
  betting: "베팅가이드",
};

export default function CommentHistory() {
  const { userId } = useParams<{ userId?: string }>();
  const { user, loading: authLoading } = useAuth();
  const targetUserId = userId || user?.id;
  const isOwnProfile = !userId || (user && userId === user.id);

  const [comments, setComments] = useState<CommentItem[]>([]);
  const [profile, setProfile] = useState<{ nickname: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchComments = useCallback(async () => {
    if (!targetUserId) return;

    try {
      const [
        { data: commentsData, error: commentsError },
        { data: profileData },
      ] = await Promise.all([
        supabase
          .from("comments")
          .select("id, content, created_at, post_id")
          .eq("author_id", targetUserId)
          .order("created_at", { ascending: false }),
        supabase
          .from("profiles")
          .select("nickname")
          .eq("id", targetUserId)
          .maybeSingle(),
      ]);

      if (commentsError) throw commentsError;

      if (profileData) {
        setProfile(profileData);
      }

      if (commentsData && commentsData.length > 0) {
        const postIds = [...new Set(commentsData.map((c) => c.post_id))];
        const { data: postsData } = await supabase
          .from("posts")
          .select("id, title, category")
          .in("id", postIds);

        const postMap = new Map<number, { title: string; category: string }>();
        (postsData || []).forEach((p) =>
          postMap.set(p.id, { title: p.title, category: p.category })
        );

        setComments(
          commentsData.map((c) => {
            const post = postMap.get(c.post_id);
            return {
              id: c.id,
              content: c.content,
              created_at: c.created_at,
              post_id: c.post_id,
              post_title: post?.title || "(삭제된 글)",
              post_category: post?.category || "",
            };
          })
        );
      }
    } catch {
      setError("댓글 내역을 불러오지 못했습니다");
    } finally {
      setLoading(false);
    }
  }, [targetUserId]);

  useEffect(() => {
    if (targetUserId) {
      fetchComments();
    }
  }, [targetUserId, fetchComments]);

  const displayName = profile?.nickname || "회원";
  const backLink = isOwnProfile ? "/profile" : `/profile/${targetUserId}`;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-foreground-600">
          <span className="w-4 h-4 flex items-center justify-center animate-spin">
            <i className="ri-loader-4-line" />
          </span>
          <span className="text-sm">로딩 중...</span>
        </div>
      </div>
    );
  }

  if (!user && !userId) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
            <span className="w-8 h-8 flex items-center justify-center text-foreground-600">
              <i className="ri-lock-line text-2xl" />
            </span>
          </div>
          <h2 className="text-lg font-bold text-foreground-700 mb-2">로그인이 필요합니다</h2>
          <p className="text-sm text-foreground-600 mb-4">댓글 내역은 로그인 후 이용할 수 있습니다</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            로그인하러 가기
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Header />

      <div className="max-w-[800px] mx-auto px-3 md:px-4 py-6">
        {/* Page Title */}
        <div className="flex items-center gap-3 mb-6">
          <Link
            to={backLink}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 border border-background-300 text-foreground-600 hover:text-foreground-600 transition-colors"
          >
            <i className="ri-arrow-left-line" />
          </Link>
          <h1 className="text-lg font-bold text-foreground-700">
            {isOwnProfile ? "내 댓글 내역" : `${displayName}님의 댓글 내역`}
          </h1>
        </div>

        {error && (
          <div className="bg-background-100 border border-red-500/20 rounded-xl px-5 py-4 mb-6 flex items-center justify-between">
            <span className="text-sm text-red-400">{error}</span>
            <button
              onClick={fetchComments}
              className="px-3 py-1.5 text-xs bg-red-500/10 text-red-400 rounded-md hover:bg-red-500/20 transition-colors whitespace-nowrap cursor-pointer"
            >
              재시도
            </button>
          </div>
        )}

        {!loading && comments.length === 0 && (
          <div className="bg-background-100 rounded-xl border border-background-300 px-5 md:px-6 py-14 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
              <span className="w-7 h-7 flex items-center justify-center text-foreground-600">
                <i className="ri-chat-off-line text-xl" />
              </span>
            </div>
            <h2 className="text-base font-bold text-foreground-700 mb-1">
              작성한 댓글이 없습니다
            </h2>
            <p className="text-sm text-foreground-600 mb-5">
              커뮤니티에서 다른 글에 댓글을 남겨보세요
            </p>
            <Link
              to="/community"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-primary-500 text-foreground-50 text-sm font-bold rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-community-line" />
              </span>
              커뮤니티 가기
            </Link>
          </div>
        )}

        {comments.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-foreground-600">
                총 <strong className="text-foreground-700">{comments.length}</strong>개의 댓글
              </p>
            </div>

            <div className="bg-background-100 rounded-xl border border-background-300 overflow-hidden divide-y divide-background-300">
              {comments.map((comment) => (
                <Link
                  key={comment.id}
                  to={`/post/${comment.post_id}`}
                  className="block px-5 md:px-6 py-4 hover:bg-background-200/50 transition-colors group"
                >
                  <p className="text-sm text-foreground-600 group-hover:text-foreground-700 transition-colors mb-2 leading-relaxed">
                    {comment.content}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-600">
                    {comment.post_category && (
                      <span className="px-2 py-0.5 text-[10px] font-bold bg-indigo-500/15 text-indigo-400 rounded whitespace-nowrap">
                        {categoryLabels[comment.post_category] || comment.post_category}
                      </span>
                    )}
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-arrow-right-up-line" />
                    </span>
                    <span className="truncate max-w-[300px] text-foreground-600">
                      {comment.post_title}
                    </span>
                    <span className="text-foreground-600 whitespace-nowrap ml-auto">
                      {formatDate(comment.created_at)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}